import LegalLayout from "@/components/LegalLayout";
import { Link } from "react-router-dom";

export default function ForJewellers() {
  return (
    <LegalLayout
      title="Facetra for Indian Jewellery Ateliers"
      lastUpdated="February 13, 2026"
      testId="page-for-jewellers"
    >
      <p>
        Facetra is the design workflow tool built for Indian jewellery ateliers — Jadau, Polki,
        Kundan, Meenakari, Temple, Rajwadi and modern Indian catalogue pieces. This page explains
        how a working atelier uses Facetra day-to-day, and where it fits alongside your CAD bench,
        karigars and client-approval workflow.
      </p>

      <h2>The problem Facetra solves for ateliers</h2>
      <p>
        The traditional sketch-to-CAD handoff is slow and expensive:
      </p>
      <ul>
        <li>Designer sketches a piece on paper (1–2 hrs)</li>
        <li>Hand-render for client review (3–4 hrs, or outsourced ₹2,000+)</li>
        <li>Client asks for revisions — repeat rendering</li>
        <li>Multi-view drawings for the CAD bench (2–3 hrs)</li>
        <li>Bill of Materials calculation (30 min–1 hr per piece)</li>
        <li>CAD operator models against the reference (1–2 days)</li>
      </ul>
      <p>
        Result: <strong>4–7 days from sketch to castable CAD file</strong>, ₹3,000–₹8,000 in
        outsourced rendering, and every client revision restarts the cycle.
      </p>

      <h2>How ateliers use Facetra</h2>

      <h3>1. Client meeting → same-day render</h3>
      <p>
        Sketch during the meeting, snap a photo, upload to Facetra, and produce a photoreal
        render before the client leaves. Show 3–4 variations of the same piece in different stone
        colours (emerald / ruby / sapphire) using the lasso refinement — each variation costs 1
        credit (~₹12) instead of 3 hours of hand-render time.
      </p>

      <h3>2. Client revisions → 5-minute turnaround</h3>
      <p>
        <em>"Make the centre stone larger, add temple motifs on the shoulder, change the
        meena colour to green"</em> — mark the regions with the lasso, describe the change,
        get a new render in 60 seconds. No more back-and-forth over WhatsApp with an outsourced
        renderer.
      </p>

      <h3>3. CAD bench handoff → drop-in ready</h3>
      <p>
        Export the CAD kit (multi-view sheet + bare-metal render + BOM + 3D mesh) and hand it
        to your CAD operator. They open the zip in Rhino / MatrixGold / JewelCAD, retopologize
        the mesh, and start modelling — no re-drawing, no back-and-forth clarification calls.
      </p>

      <h3>4. Karigar handoff → BOM + wall-thickness cheatsheet</h3>
      <p>
        The CAD kit's README ships with wall-thickness recommendations (DMLS 1–2mm, SLA 0.5mm,
        FDM 0.8–1.2mm, lost-wax 0.6–0.8mm) and a one-sided-shell workflow specifically for
        temple / animal / relief pieces. Your karigar sees exactly what to build.
      </p>

      <h3>5. Wholesale catalogue → PhotoRoom bulk render</h3>
      <p>
        Have hundreds of raw DSLR photographs of finished pieces? PhotoRoom converts them to
        studio-quality HD (4K) or 8K renders in bulk, on a solid matte-black backdrop, ready
        for your e-commerce site, catalogue PDF, or Instagram feed.
      </p>

      <h2>What Facetra saves you</h2>

      <p>Per-piece savings (typical Kundan Bali):</p>
      <ul>
        <li><strong>Design time</strong>: 6 hours → 30 minutes (a designer can service 6× more clients per week)</li>
        <li><strong>Rendering cost</strong>: ₹2,000–₹5,000 outsourced → ~₹256 in Facetra credits</li>
        <li><strong>Time to castable CAD</strong>: 4–7 days → same day</li>
        <li><strong>Client approval cycle</strong>: 3–5 revisions × 1 day each → 3–5 revisions × 5 minutes</li>
      </ul>

      <h2>What Facetra does NOT do</h2>
      <ul>
        <li>Cast or manufacture jewellery — you still need your karigars and CAD bench.</li>
        <li>Replace Rhino / MatrixGold / JewelCAD — Facetra is a workflow layer <em>before</em> CAD.</li>
        <li>Retopologize meshes for direct casting — your CAD operator does that final step.</li>
      </ul>

      <h2>Which plan do most ateliers pick?</h2>
      <p>
        Small ateliers (2–5 designers) usually start on the <strong>Studio pack (₹7,000 for 500
        credits)</strong> — enough for ~150 finished CAD kits. Medium ateliers (5–15 designers)
        move to the <strong>Team pack (₹7,499 for 1,000 credits)</strong>. Larger houses with
        dedicated catalogue photography use the <strong>Enterprise annual plan</strong> which
        includes PhotoRoom bulk credits and dedicated support — contact hello@facetra.studio.
      </p>

      <h2>Frequently asked questions from ateliers</h2>

      <h3>Do we need to change our CAD operator's tools?</h3>
      <p>
        No. Facetra outputs standard formats (PNG, CSV, GLB, STL, OBJ, FBX) that open in every
        professional jewellery CAD tool — Rhino, MatrixGold, JewelCAD, Firestorm.
      </p>

      <h3>How do we handle client confidentiality?</h3>
      <p>
        Every design is stored in a private per-atelier bucket. Nothing is public. Nothing is
        shared with other Facetra users. Nothing is used to train shared AI models. See our{" "}
        <Link to="/privacy">privacy policy</Link>.
      </p>

      <h3>Can we invite multiple designers to one account?</h3>
      <p>
        Yes — the Team and Enterprise plans include multi-seat access with per-designer usage
        tracking.
      </p>

      <h3>How does the live gold rate work?</h3>
      <p>
        Facetra fetches the current 22kt yellow gold rate every hour from a live commodity feed
        and applies it automatically to every BOM. Client quotes are never stale.
      </p>

      <h3>Do we need to teach our designers new software?</h3>
      <p>
        Facetra runs entirely in the browser and works exactly like uploading a photo. Most
        designers are productive within 15 minutes. The vocabulary (Jadau, Polki, chaton,
        bezel, jaali, meena work) is what they already speak.
      </p>

      <h2>Design by style</h2>
      <p>
        Facetra ships with dedicated prompt libraries for the major Indian jewellery styles.
        Pick your specialisation to see a workflow, sample prompts and pricing tuned to that
        style:
      </p>
      <ul className="not-prose grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 mb-8">
        <li>
          <Link
            to="/ai-kundan-jewellery-design"
            data-testid="for-jewellers-style-kundan"
            className="block rounded-xl border border-white/8 bg-[#0E0E0E] p-4 hover:border-[#D4AF37]/30 transition"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-1">Kundan</div>
            <div className="text-[13px] text-white leading-snug">AI Tool for Kundan Jewellery Design</div>
          </Link>
        </li>
        <li>
          <Link
            to="/ai-jadau-jewellery-design"
            data-testid="for-jewellers-style-jadau"
            className="block rounded-xl border border-white/8 bg-[#0E0E0E] p-4 hover:border-[#D4AF37]/30 transition"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-1">Jadau</div>
            <div className="text-[13px] text-white leading-snug">AI Tool for Jadau Jewellery Design</div>
          </Link>
        </li>
        <li>
          <Link
            to="/ai-polki-jewellery-design"
            data-testid="for-jewellers-style-polki"
            className="block rounded-xl border border-white/8 bg-[#0E0E0E] p-4 hover:border-[#D4AF37]/30 transition"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-1">Polki</div>
            <div className="text-[13px] text-white leading-snug">AI Tool for Polki Diamond Jewellery Design</div>
          </Link>
        </li>
        <li>
          <Link
            to="/ai-diamond-jewellery-design"
            data-testid="for-jewellers-style-diamond"
            className="block rounded-xl border border-white/8 bg-[#0E0E0E] p-4 hover:border-[#D4AF37]/30 transition"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-1">Diamond</div>
            <div className="text-[13px] text-white leading-snug">AI Tool for Diamond Jewellery Design</div>
          </Link>
        </li>
        <li>
          <Link
            to="/ai-temple-jewellery-design"
            data-testid="for-jewellers-style-temple"
            className="block rounded-xl border border-white/8 bg-[#0E0E0E] p-4 hover:border-[#D4AF37]/30 transition"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-1">Temple</div>
            <div className="text-[13px] text-white leading-snug">AI Tool for Temple Jewellery Design</div>
          </Link>
        </li>
      </ul>

      <div className="mt-10 rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/10 to-transparent p-6 not-prose">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-2">For ateliers</div>
        <h3 className="font-display text-2xl mb-3">Book a demo for your atelier</h3>
        <p className="text-sm text-white/70 mb-4">
          We'll walk your design lead and CAD operator through a real client brief — sketch to
          CAD kit — in under 30 minutes. Free, no commitment.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/signup"
            data-testid="for-jewellers-signup"
            className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium px-5 py-2.5 hover:bg-[#F3CE5A] transition"
          >
            Try Facetra free →
          </Link>
          <a
            href="mailto:hello@facetra.studio?subject=Atelier demo request"
            data-testid="for-jewellers-demo"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 text-white text-sm px-5 py-2.5 hover:border-[#D4AF37]/50 hover:text-[#D4AF37] transition"
          >
            Book atelier demo
          </a>
        </div>
      </div>
    </LegalLayout>
  );
}
